/**
 * Home's content cards: the slate, the picks, the money, the night.
 *
 * Split from `Home.tsx` alongside `HomeParts.tsx`; see the note at the top of that file for why.
 */

import { useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { faab as faabAmt, WAGER_ODDS } from '../domain/faab.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { bothZones } from '../domain/deadlines.js';
import { Citations, Rule } from './RuleText.js';
import { GmBadge } from './GmBadge.js';
import { NflMark } from './NflChip.js';
import { PosChip } from './chips.js';

/* ------------------------------------------------------------------------------- the slate */

export type SlateStatus = 'none' | 'draft' | 'submitted';

export interface SlateRow {
  player: string;
  pos: string;
  /** The round he is actually charged to, or null when the app could not place him. */
  round: number | null;
  tagged: boolean;
}

/**
 * The keeper slot pips: eight of them, with a rule where the tax starts.
 *
 * THE TAX EXPLAINED BY DESIGN RATHER THAN BY A SENTENCE. Four slots are free and the fifth starts
 * costing (2020-III-1 §2, 2023 §2), so the boundary is drawn INSIDE the thing that counts the
 * slots. A manager who is about to cross it can see that he is about to cross it, which is what
 * the paragraph this replaces was trying to say.
 *
 * A franchise tag eats three slots, so the pip count is slots used and not names on the card.
 */
export function SlotPips({ used, max, free }: { used: number; max: number; free: number }) {
  return (
    <span className="pips" role="img" aria-label={`${used} of ${max} keeper slots used`}>
      {Array.from({ length: max }, (_, i) => (
        <span
          key={i}
          className={`pip${i < used ? ' on' : ''}${i + 1 === free + 1 ? ' taxstart' : ''}`}
        />
      ))}
    </span>
  );
}

/**
 * My keeper slate.
 *
 * DRAFT IS THE STATE THIS CARD EXISTS FOR. A manager who thinks he has declared and has not is the
 * failure it is here to prevent, so the pill says `DRAFT` in ink outline rather than in amber —
 * incomplete is not urgent — and the foot says in words that nothing has been counted yet.
 *
 * THE COST IS A CODE. `Rd7`, and `×3` on the tag chip where a franchise player eats three slots.
 * Four rows of codes can be read down the column in one movement; four rows of "round 7 cost · 3
 * slots" have to be read as sentences, and the word "cost" appearing three times distinguishes
 * nothing. An unplaceable keeper is an amber `!` rather than a dash, because a dash reads as a
 * known nothing and he is not free: the app could not find a pick to charge him to, and that is
 * something the GM has to fix before the deadline.
 */
export function SlateCard(
  { status, rows, slots, tax, charged, balance, deadline, onGo }: {
    status: SlateStatus;
    rows: readonly SlateRow[];
    slots: number;
    tax: number;
    charged: boolean;
    balance: number;
    deadline?: string | null;
    onGo: () => void;
  },
) {
  return (
    <section className="card slate">
      <div className="cardhead">
        <h3>My keeper slate</h3>
        <span className={`pill outline ${status}`}>
          {status === 'submitted' ? 'submitted' : status === 'draft' ? 'draft' : 'not started'}
        </span>
        <button type="button" className="linkbtn" onClick={onGo}>
          {status === 'none'
            ? (deadline ? 'Declare' : 'Open')
            : deadline ? `Edit until ${dayMonth(deadline)}` : 'Open'}
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="fact empty">Nothing declared yet.</p>
      ) : (
        <ul className="slaterows">
          {rows.map((k) => (
            <li key={k.player}>
              <span className="who">
                <b>{surname(k.player)}</b>
                <span className="given">{given(k.player)}</span>
              </span>
              {k.pos && <PosChip pos={k.pos} />}
              <NflMark player={k.player} />
              {k.tagged && <span className="tagchip">FRANCHISE <i>×3</i></span>}
              {k.round === null
                ? (
                  <span
                    className="unplaced"
                    role="img"
                    aria-label="No pick left to charge him to"
                    title="No pick left to charge him to. Change a round on the declare screen."
                  >!</span>
                )
                : <span className="cost">Rd{k.round}</span>}
            </li>
          ))}
        </ul>
      )}

      <div className="slatefoot">
        <SlotPips used={slots} max={LEAGUE_2026.maxKeepers} free={LEAGUE_2026.freeKeepers} />
        <span className={`taxfig${tax > 0 ? ' owed' : ''}`}>
          {tax === 0 ? 'no luxury tax' : <>tax <b>{faabAmt(tax)}</b></>}
        </span>
      </div>
      {status === 'draft' && (
        <p className="fact warnline">Not visible to the league, and not counted until submitted.</p>
      )}
      {tax > 0 && (
        <p className="fact">
          {charged
            ? `Already charged. Your ${faabAmt(balance)} is after the tax.`
            : `${faabAmt(balance)} now, ${faabAmt(balance - tax)} once the draft makes them official.`}
        </p>
      )}
      <div className="cardfoot"><Citations ids={['2023-keeper-expansion-eight']} /></div>
    </section>
  );
}

/*
 * SURNAME FIRST AND LOUDEST, first name after in a lighter weight. The wall does the same thing in
 * two lines; this does it in one. Both put the half a reader is scanning for in the dominant
 * position, which is the only part that has to match.
 */
const surname = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return (parts.length > 1 ? parts.slice(1).join(' ') : parts[0] ?? '').toUpperCase();
};
const given = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? parts[0]! : '';
};
const dayMonth = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short' });

/* ------------------------------------------------------------------- what is left after keepers */

export type PickCell = 'open' | 'keeper' | 'gone';

/**
 * Sixteen rounds, and what each one is now.
 *
 * THIS REPLACES THREE STAT TILES that restated numbers a manager could already read elsewhere. A
 * declaration is a trade against your own draft, and this is the other side of it: the picks that
 * survive it, the ones a keeper has taken, and the ones that are somebody else's because you sold
 * them. Nothing else on Home says the third thing at all.
 */
export function PicksStrip(
  { cells, balance, onGo }: {
    cells: readonly PickCell[];
    balance: number;
    onGo: () => void;
  },
) {
  const open = cells.filter((c) => c === 'open').length;
  return (
    <section className="card picks">
      <div className="cardhead">
        <h3 className="peyebrow">Your picks after keepers</h3>
        <button type="button" className="linkbtn faabfig" onClick={onGo}>
          FAAB <b>{faabAmt(balance)}</b>
        </button>
      </div>
      <ol
        className="pickstrip"
        role="img"
        aria-label={`${open} picks still open of ${cells.length} rounds`}
      >
        {cells.map((c, i) => (
          <li key={i} className={c}>
            <span className="cell" />
            <span className="rd">{i + 1}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ---------------------------------------------------------------------------------- the farm */

export function FarmCard(
  { rows, max, showOpen, onGo }: {
    rows: ReadonlyArray<{ player: string; pos?: string; school?: string }>;
    max: number;
    /** Open slots are drawn on draft day, when they are about to be fillable, and not before. */
    showOpen: boolean;
    onGo: () => void;
  },
) {
  return (
    <section className="card farm">
      <div className="cardhead">
        <h3>My farm</h3>
        <span className="pill outline">{rows.length} of {max}</span>
        <button type="button" className="linkbtn" onClick={onGo}>Farm rounds</button>
      </div>
      <ol className="farmrows">
        {rows.map((f, i) => (
          <li key={f.player}>
            <span className="n">{i + 1}</span>
            <span className="who">
              <b>{surname(f.player)}</b>
              <span className="given">{given(f.player)}</span>
            </span>
            {f.pos && <PosChip pos={f.pos} />}
            {f.school && <span className="school">{f.school}</span>}
          </li>
        ))}
        {showOpen && Array.from({ length: Math.max(0, max - rows.length) }, (_, i) => (
          <li key={`open-${i}`} className="open">
            <span className="n">{rows.length + i + 1}</span>
            <span className="who">Open — bid in the farm rounds</span>
          </li>
        ))}
      </ol>
      <div className="cardfoot"><Citations ids={['2022-farm-system-clarification']} /></div>
    </section>
  );
}

/* -------------------------------------------------------------------------------- the money */

export function FaabLead(
  { balance, place, tied, entries, onGo }: {
    balance: number;
    place: number;
    tied: boolean;
    entries: ReadonlyArray<{ id: string; date: string; description: string; amount: number }>;
    onGo: () => void;
  },
) {
  return (
    <section className="card lead faablead">
      <div className="cardhead">
        <h3>FAAB</h3>
        <button type="button" className="linkbtn" onClick={onGo}>The ledger</button>
      </div>
      <p className="bigfig">
        <b>{faabAmt(balance)}</b>
        <span className="fact"> · {tied ? 'tied ' : ''}{ordinal(place)} of {LEAGUE_2026.teams}</span>
      </p>
      <ul className="ledgerlines">
        {entries.map((e) => (
          <li key={e.id}>
            <span className="when">{dayMonth(e.date)}</span>
            <span className="what">{e.description}</span>
            <span className={`amt ${e.amount < 0 ? 'spend' : 'credit'}`}>
              {e.amount < 0 ? '−' : '+'}{faabAmt(Math.abs(e.amount))}
            </span>
          </li>
        ))}
        {entries.length === 0 && <li className="empty">Nothing moved yet this season.</li>}
      </ul>
    </section>
  );
}

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
};

export function WageringCard() {
  return (
    <section className="card wagering quiet">
      <div className="cardhead">
        <h3>In-season wagering</h3>
        <span className="peyebrow">from week 9</span>
        <Rule id="2023-in-season-wagering" />
      </div>
      <p className="fact">
        One wager a week, {faabAmt(100)} max, on an upset in a matchup you are not in.
      </p>
      <ul className="odds">
        {Object.entries(WAGER_ODDS).map(([gap, odds]) => (
          <li key={gap}>+{odds}</li>
        ))}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------------------------------- the trades */

/*
 * THE LEAGUE'S TRADES, AND WHICH OF THEM ARE YOURS (Tim, 8 September: "show pending trades not
 * just that the active GM is involved in on the home page").
 *
 * `open` is every deal in flight the viewer is allowed to see; `mine` is how many of those he is
 * in. Both, because they are two different questions and the card used to answer only the second
 * one — on a night with three live trades and none of them his, it said "none open".
 */
export function TradesCard(
  { open, mine, draftDay, onGo }: {
    open: number; mine: number; draftDay: boolean; onGo: () => void;
  },
) {
  return (
    <section className="card trades">
      <div className="cardhead">
        <h3>
          Trades · <b>{open === 0 ? 'none' : open}</b> open
          {open > 0 && (
            <span className="mut">
              {' · '}
              {mine === 0 ? 'none involving your team'
                : mine === open ? (open === 1 ? 'yours' : 'all yours')
                  : `${mine} involving your team`}
            </span>
          )}
        </h3>
        <button type="button" className="linkbtn" onClick={onGo}>
          {open > 0 ? 'Open it' : 'Propose one'}
        </button>
      </div>
      <p className="fact">
        {draftDay
          ? 'Draft-day trades close ten picks before the earliest pick involved'
          : 'A submitted trade runs a 24-hour window: 20 hours to counter, then the league votes'}
      </p>
    </section>
  );
}

/* --------------------------------------------------------------------------- the year that went */

export function YearInReview(
  { season, top, mine, onGo }: {
    season: number;
    top: ReadonlyArray<{ gm: string; rank: number; w: number; l: number; pf: number }>;
    mine: { rank: number; w: number; l: number; pf: number } | null;
    onGo: () => void;
  },
) {
  return (
    <section className="card lead yir">
      <div className="yirmain">
        <div className="cardhead">
          <h3>Last season in review</h3>
          <button type="button" className="linkbtn" onClick={onGo}>Standings</button>
        </div>
        <ol className="podium">
          {top.map((r) => (
            <li key={r.gm}>
              <span className="slotno">{r.rank}</span>
              <GmBadge gm={r.gm} />
              <b>{managerShort(r.gm)}</b>
              <span className="rec">{r.w}–{r.l}</span>
              <span className="pf">{Math.round(r.pf).toLocaleString('en-US')}</span>
            </li>
          ))}
        </ol>
      </div>
      {mine && (
        <div className="yirmine">
          <span className="peyebrow">Your season</span>
          <b className="bigfig">{ordinal(mine.rank)}</b>
          <span className="fact">
            {mine.w}–{mine.l} · {Math.round(mine.pf).toLocaleString('en-US')} points for
          </span>
        </div>
      )}
      <span className="srmute">{season}</span>
    </section>
  );
}

/* ----------------------------------------------------------------------------- the committee */

export function CommitteeCard(
  { iso, link, left, docket, lotteryRun, onGo }: {
    iso: string | null;
    link: string | null;
    left: string | null;
    docket: number;
    lotteryRun: boolean;
    onGo: (s: string, p: string) => void;
  },
) {
  const isUrl = Boolean(link && /^https?:\/\//i.test(link.trim()));
  return (
    <section className="card committee">
      <div className="cardhead">
        <h3>Competition Committee</h3>
        {left && <span className="pill outline">{left}</span>}
        <button type="button" className="linkbtn" onClick={() => onGo('league', 'proposals')}>
          The docket
        </button>
      </div>
      <p className="cmtewhen">{iso ? bothZones(iso) : 'The commissioners have not set a date.'}</p>
      {link
        ? (isUrl
          ? (
            <a className="btn primary wide" href={link.trim()} target="_blank" rel="noreferrer noopener">
              Join the meeting
            </a>
          )
          : <p className="fact"><b>{link}</b></p>)
        : <p className="fact">No link posted yet.</p>}
      <p className="fact">
        <b>{docket}</b> on the docket.{' '}
        {lotteryRun
          ? 'The lottery has been drawn.'
          : 'The lottery is drawn on the night, before any vote.'}
      </p>
    </section>
  );
}

/* -------------------------------------------------------------------------- the farm targets */

export interface TargetRow {
  player: string;
  position?: string;
  school?: string;
  note?: string;
  /** Set once somebody has nominated him, which ends the watch. */
  nominatedBy?: string | null;
}

/**
 * A private list of college players you are watching, and the one card on Home you can write to.
 *
 * PRIVATE IS ENFORCED, NOT ASSERTED. `firestore.rules` refuses every read of this document but its
 * owner's, commissioners and the admin included, and it is the only collection in that file with
 * no commissioner exception. The reason is narrow: everything else private opens to him because he
 * corrects the record on somebody's behalf, and there is nothing to correct in a watch list. It is
 * what a manager intends to bid on, in an auction the commissioner is running.
 *
 * A ROW STRIKES THROUGH WHEN THE PLAYER IS NOMINATED, and the strikethrough is derived from the
 * public nomination log rather than from anybody's list. Watching him is over at that point: the
 * auction has him, and what happens next is bidding rather than watching.
 *
 * DELIBERATELY NOT A BID, A PRICE OR A RANK. The farm auction is live and open-outcry; anything
 * the app could enforce would be a second auction running beside the real one.
 */
export function FarmTargetsCard(
  { rows, onAdd, onRemove, onGo }: {
    rows: readonly TargetRow[];
    onAdd: () => void;
    onRemove: (player: string) => void;
    onGo: () => void;
  },
) {
  return (
    <section className="card targets">
      <div className="cardhead">
        <h3>Farm targets</h3>
        <span className="pill outline">private</span>
        <button type="button" className="linkbtn" onClick={onGo}>Player index</button>
      </div>
      <ul className="targetrows">
        {rows.map((t) => (
          <li key={t.player} className={t.nominatedBy ? 'gone' : ''}>
            <span className="who">
              <b>{surname(t.player)}</b>
              <span className="given">{given(t.player)}</span>
            </span>
            {t.position && <PosChip pos={t.position} />}
            {t.school && <span className="school">{t.school}</span>}
            {t.nominatedBy
              ? <span className="nominated">nominated by {managerShort(t.nominatedBy)}</span>
              : (
                <button type="button" className="drop" aria-label={`Stop watching ${t.player}`}
                  onClick={() => onRemove(t.player)}>×</button>
              )}
            {t.note && !t.nominatedBy && <span className="tnote">{t.note}</span>}
          </li>
        ))}
        {rows.length === 0 && (
          <li className="empty">Nobody yet. Tag a player and he shows here on draft night.</li>
        )}
      </ul>
      <button type="button" className="addtarget" onClick={onAdd}>+ Tag a player</button>
    </section>
  );
}

/**
 * Tagging one, inline on the card rather than on another screen.
 *
 * THE PLAYER INDEX IS COMMISSIONER-GATED, so "go there and tag him" is a door nine of the ten
 * cannot open. The list a manager is tagging FROM is public information — it is the 2027 prospect
 * board — so the field carries it as a datalist and the whole interaction stays on the card he is
 * looking at. The header still links to the index for anybody who can open it.
 *
 * A 16px field, because iOS zooms the page on anything smaller and this one is on a phone on
 * draft night by definition.
 */
export function TargetAdd(
  { names, onAdd, onCancel }: {
    names: readonly string[];
    onAdd: (player: string, note: string) => void;
    onCancel: () => void;
  },
) {
  const [player, setPlayer] = useState('');
  const [note, setNote] = useState('');
  const known = names.includes(player.trim());
  return (
    <form
      className="targetadd"
      onSubmit={(e) => { e.preventDefault(); if (known) onAdd(player.trim(), note.trim()); }}
    >
      <label>
        <span className="peyebrow">Player</span>
        <input
          list="ffl-prospects"
          value={player}
          autoFocus
          placeholder="Start typing a name"
          onChange={(e) => setPlayer(e.target.value)}
        />
      </label>
      <datalist id="ffl-prospects">
        {names.map((n) => <option key={n} value={n} />)}
      </datalist>
      <label>
        <span className="peyebrow">Note</span>
        <input
          value={note}
          maxLength={60}
          placeholder="Optional, one line"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <div className="targetacts">
        <button type="submit" className="btn primary" disabled={!known}>Tag him</button>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
      </div>
      {/* Blank beats partial: a name the board does not carry is not tagged, and says so. */}
      {player.trim().length > 2 && !known && (
        <p className="fact">Not on the 2027 board. Check the spelling.</p>
      )}
    </form>
  );
}

/* ------------------------------------------------------------------------- draft-day panels */

export interface RundownRow {
  code: string;
  round: number;
  player?: string;
  position?: string;
  keeper?: boolean;
  franchise?: boolean;
  /** A farm graduate: he cost this pick under 2013-III-1 §4, and the board frames him violet. */
  farm?: boolean;
  /** The pick on the clock right now. */
  onClock?: boolean;
}

/**
 * Your own picks for the night, in order, with the rounds you no longer hold simply absent.
 *
 * A TRADED ROUND IS NOT A GAP. It belongs to somebody else, and drawing it as an empty row of
 * yours would be the board telling you that you are up in a round you sold in November. The count
 * in the header is the honest total: five of fourteen, not five of sixteen.
 *
 * A FUTURE ROUND IS A DASHED LINE rather than a blank, because "you have a pick here and it has
 * not happened yet" is a different thing from "there is nothing here".
 */
export function PickRundown(
  { rows, made, total }: {
    rows: readonly RundownRow[];
    made: number;
    total: number;
  },
) {
  /*
   * EVERY PICK, ALL SIXTEEN ROUNDS (Tim, 8 September: "show all picks on the home page pick
   * rundown").
   *
   * This used to fold everything from round eight down into one line reading "6 picks open, 3
   * already yours". The fold was a phone measure that was never conditional on being on a phone,
   * so a desktop reading of the card — which is where a man actually plans his night — showed him
   * seven rounds and a summary of the other nine. The rundown is the one card whose whole job is
   * "what have I got left", and half of it was a sentence.
   */
  const shown = rows;
  return (
    <section className="card rundowncard">
      <div className="cardhead">
        <h3>Pick rundown</h3>
        <span className="pill outline">{made} of {total}</span>
      </div>
      <ul className="rundown">
        {shown.map((r) => (
          <li key={r.code} className={r.onClock ? 'onclockrow' : r.player ? '' : 'future'}>
            <span className="code">{r.code}</span>
            {r.onClock ? (
              <span className="who"><b>on the clock</b></span>
            ) : r.player ? (
              <>
                <span className="who">
                  <b>{surname(r.player)}</b>
                  <span className="given">{given(r.player)}</span>
                </span>
                {/*
                  THREE WAYS A MAN CAN ALREADY BE YOURS, and the board draws all three, so this
                  does too (Tim, 8 September). A farm graduate is not a keeper — his cost is
                  computed a different way (2013-III-1 §4) — and the tag is not either.
                */}
                {r.franchise ? <span className="keeperpill">FRANCHISE</span>
                  : r.farm ? <span className="keeperpill farm">FARM</span>
                    : r.keeper ? <span className="keeperpill">KEEPER</span> : null}
                {r.position && <PosChip pos={r.position} />}
                <NflMark player={r.player} />
              </>
            ) : <span className="blank" aria-label="not yet picked" />}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * What you have actually drafted, by position.
 *
 * NO INVENTED SLOT COUNTS. A roster requirement is a Yahoo setting this app does not model, so a
 * row saying "RB 2 of 4" would be the app asserting a rule it has not read. Drafted players only,
 * and a group with nobody in it is one dashed blank rather than a row of empty boxes.
 *
 * No kicker: this league does not use one (2019-III-1 §3).
 *
 * IN ADP ORDER WITHIN A POSITION (Tim, 8 September: "as roster is filled in, the players are to
 * be ordered by their ADP"). A roster in the order the picks happened to be made is a list of
 * accidents; in consensus order it is a read on the team. `Home` does the sorting, because it is
 * the screen that has the ADP file.
 */
export function RosterByPosition(
  { groups, made, total }: {
    groups: ReadonlyArray<{
      pos: string;
      players: ReadonlyArray<{ player: string; keeper?: boolean; franchise?: boolean; farm?: boolean }>;
    }>;
    made: number;
    total: number;
  },
) {
  return (
    <section className="card byposition">
      <div className="cardhead">
        <h3>Roster by position</h3>
        <span className="pill outline">{made} of {total}</span>
      </div>
      <ul className="posgroups">
        {groups.map((g) => (
          <li key={g.pos}>
            <PosChip pos={g.pos} />
            <ol>
              {g.players.map((p) => (
                <li key={p.player}>
                  <span className="who">
                    <b>{surname(p.player)}</b>
                    <span className="given">{given(p.player)}</span>
                  </span>
                  {p.franchise ? <span className="keeperpill">FRANCHISE</span>
                    : p.farm ? <span className="keeperpill farm">FARM</span>
                      : p.keeper ? <span className="keeperpill">KEEPER</span> : null}
                  <NflMark player={p.player} />
                </li>
              ))}
              {g.players.length === 0 && <li className="blankrow"><span className="blank" /></li>}
            </ol>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The next ten picks, so a manager can see how long he has.
 *
 * NAMES ONLY, NO MANAGER MARKS. Every draft surface in this app follows that rule: the identity
 * hues are pale by design and a pale circle is illegible on the television the board is shown on.
 */
export function NextTen(
  { rows, me, onGo }: {
    rows: ReadonlyArray<{ code: string; gm: string; clock?: string | null }>;
    me: string;
    onGo: () => void;
  },
) {
  return (
    <section className="card nextten quiet">
      <div className="cardhead">
        <h3>Next 10 picks</h3>
        <button type="button" className="linkbtn" onClick={onGo}>Full board</button>
      </div>
      <ol className="tenlist">
        {rows.map((r) => (
          <li key={r.code} className={r.gm.toUpperCase() === me ? 'mine' : ''}>
            <span className="code">{r.code}</span>
            <span className="who">{managerShort(r.gm)}</span>
            {r.gm.toUpperCase() === me && <span className="youpill">YOU</span>}
            {r.clock && <span className="clockfig">{r.clock}</span>}
          </li>
        ))}
      </ol>
    </section>
  );
}
